// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { getInfoCardStyles, InfoCardStyles } from "@mcmc/App/Widgets/InfoCard/InfoCard.style";
import LazyImage from "@mcmc/MobiusOverrides/LazyImage";
import MultilineEllipsis from "@mcmc/XDL/Components/Common/MultiLineEllipsis";
import { ellipsis, extendDataLayerWithEnvent, mergeWithCss } from "@mcmc/XDL/Utilities";
import parse from "html-react-parser";
import React, { createRef, useState } from "react";
import { css } from "styled-components";

export const enum fields {
    imageUrl = "imageUrl",
    imageCaption = "imageCaption",
    link = "link",
    linkText = "linkText",
    linkAlignment = "linkAlignment",
    copy = "copy",
    openInNewTab = "openInNewTab",
    maxNumberOfCopyRows = "maxNumberOfCopyRows",
    isSolutionsWidget = "isSolutionsWidget",
}

export type InfoCardProps = {
    [fields.imageUrl]: string;
    [fields.imageCaption]: string;
    [fields.copy]: string;
    [fields.linkText]: string;
    [fields.linkAlignment]: "left" | "center" | "right";
    [fields.openInNewTab]: boolean;
    [fields.isSolutionsWidget]: boolean;
    url: string | undefined;
    direction: "vertical" | "horizontal";
    maxNumberOfCopyRows?: number;
    extendedStyles?: InfoCardStyles;
};

const InfoCard: React.FC<InfoCardProps> = ({
    imageUrl,
    imageCaption,
    copy,
    linkAlignment,
    linkText,
    openInNewTab,
    url,
    direction,
    maxNumberOfCopyRows,
    children,
    extendedStyles,
    isSolutionsWidget,
}) => {
    const [styles] = useState(() => {
        return mergeWithCss(getInfoCardStyles(direction), extendedStyles);
    });
    const imgWrapperRef = createRef<HTMLImageElement>();
    function setBackgroundImage() {
        if (imgWrapperRef.current) {
            imgWrapperRef.current.style.backgroundImage = `url(${imageUrl})`;
        }
    }

    const copyTextStyles = {
        ...styles.copyText,
        css: css`
            ${styles.copyText?.css || ""}
            ${maxNumberOfCopyRows ? ellipsis(maxNumberOfCopyRows, 20) : null}
            @media print {
                ${ellipsis(4, 20)};
                height: 80px;
            }
        `,
    };

    const InfoCardWrapper = url ? Link : StyledWrapper;
    const infoCardWrapperClassName = url ? `linkable-wrapper` : "";

    const handleInfoCardClick = () => {
        if (!isSolutionsWidget) {
            return;
        }

        extendDataLayerWithEnvent("service-wrapper");
    };

    const infoCardId = `learnMore_${imageCaption.replace(/\s+/g, '')}`;
    return (
        <InfoCardWrapper
            href={url}
            id={`${infoCardId}_CardWrapper`}
            target={openInNewTab ? "_blank" : undefined}
            aria-label={imageCaption}
            className={infoCardWrapperClassName}
            onClick={handleInfoCardClick}
            {...styles.wrapper}
        >
            <StyledWrapper ref={imgWrapperRef} className="bg-image">
                {imageUrl && <LazyImage src={imageUrl} onLoad={setBackgroundImage} {...styles.hiddenImage} />}
                {!imageUrl && (
                    <StyledWrapper aria-hidden="true" {...styles.noImageWrapper}>
                        <Icon {...styles.noImageIcon} />
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.textContentWrapper} id={`${infoCardId}_textWrapper`}>
                <MultilineEllipsis content={imageCaption} maxLines={2} {...styles.heading} />
                {children && <StyledWrapper {...styles.childrenWrapper}>{children}</StyledWrapper>}
                {copy && <Typography {...copyTextStyles}>{parse(copy, parserOptions)}</Typography>}
                {url && linkText && (
                    <StyledWrapper {...styles.linkStyles.alignments[linkAlignment]} id={`${infoCardId}_linkWrapper`}>
                        <Clickable className="read-more-link" {...styles.linkStyles.anchor} id={`${infoCardId}_link`}>
                            {linkText}
                        </Clickable>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </InfoCardWrapper>
    );
};

export default InfoCard;
